import type { ChoiceGroup, FoodOption, Meal, MealMenu } from "@/data/meal-plan";
import type { ChoiceGroupSelections, MealSelections, Selection } from "@/types/meal-plan-state";
import { createMealSelections, getMealChoices } from "./random-plan";

/**
 * Keyword → canonical ingredient name mapping used by the Comidas screen
 * to auto-fill lunch/dinner ingredient pickers from a free-text note.
 *
 * Keys MUST match FoodOption.name exactly (as defined in data/meal-plan
 * options files). Keywords are matched case- and accent-insensitively.
 * Includes common Peruvian dish names so descriptions like
 * "lomo saltado con papas fritas" resolve to Carne de res + Papa.
 */
export const INGREDIENT_KEYWORDS: Record<string, string[]> = {
  // ── Proteins ──
  "Carne de res": [
    "carne de res",
    "carne",
    "res",
    "bistec",
    "bife",
    "asado de res",
    "asado",
    "lomo saltado",
    "lomo",
    "saltado",
    "estofado de res",
    "seco de res",
    "seco",
    "anticucho",
    "parrilla",
    "churrasco",
  ],
  "Carne molida": ["carne molida", "molida", "picadillo", "albondigas", "albóndigas"],
  "Pollo": [
    "pollo",
    "pechuga",
    "pechuga de pollo",
    "pollo a la brasa",
    "brasa",
    "aji de gallina",
    "ají de gallina",
    "arroz con pollo",
    "pollo al sillao",
    "pollo al horno",
    "tallarin con pollo",
    "tallarín con pollo",
    "saltado de pollo",
    "milanesa de pollo",
    "pollo frito",
  ],
  "Pescado": [
    "pescado",
    "tilapia",
    "filete",
    "filete de pescado",
    "sudado de pescado",
    "sudado",
    "ceviche",
    "ceviche de pescado",
    "jalea",
    "chicharron de pescado",
    "chicharrón de pescado",
    "pescado frito",
    "trucha",
    "salmon",
    "salmón",
  ],
  // "lomo de cerdo" outranks "lomo" (Carne de res) by length.
  "Cerdo": ["cerdo", "chancho", "lomo de cerdo", "chuleta", "chuleta de cerdo"],
  "Pavita": ["pavita", "pavo", "pechuga de pavita", "pechuga de pavo"],
  "Atún en conserva": ["atun", "atún", "atun en conserva", "atún en conserva"],
  "Huevo entero": ["huevo", "huevos", "tortilla", "tortilla de huevo", "omelette", "revuelto"],
  "Claras de huevo": ["claras", "clara de huevo", "claras de huevo"],
  "Yogurt griego Vakimu": ["yogurt", "yogur", "yogurt griego", "vakimu griego"],
  "Queso fresco": ["queso fresco"],
  "Queso cottage": ["queso cottage", "cottage"],
  "Queso mozzarella": ["mozzarella", "queso mozzarella"],
  "Vakimu Pro": ["vakimu pro", "vakimu"],

  // ── Carbs ──
  "Papa": ["papa", "papas", "papas fritas", "pure de papa", "puré de papa", "papa dorada"],
  "Arroz blanco": ["arroz", "arroz blanco"],
  "Arroz integral": ["arroz integral", "integral"],
  "Lentejas": ["lenteja", "lentejas"],
  "Frejol": ["frejol", "frejoles", "frijol", "frijoles", "frijolada"],
  "Arvejita partida": ["arveja", "arverja", "arvejita", "arvejitas", "arvejas", "arverjas"],
  "Garbanzo": ["garbanzo", "garbanzos"],
  "Yuca": ["yuca", "yuquitas", "yuca frita"],
  "Camote": ["camote"],
  "Choclo": ["choclo", "maiz", "maíz", "choclito"],
  "Fideos blancos": ["fideos", "fideo", "tallarin", "tallarín", "tallarines", "pasta", "spaghetti", "espagueti"],
  "Fideos integrales": ["fideos integrales", "pasta integral", "tallarin integral", "tallarín integral"],
  "Guiso (locro, olluquito, etc.)": ["guiso", "locro", "olluquito", "guisado", "olluco"],
  "Avena cruda (cocinar)": ["avena"],
  "Quinua cruda (cocinar)": ["quinua", "quinoa"],
  // Cooked quinua (lunch/dinner). Where both are offered, a bare "quinua"
  // still goes to the raw option listed first; "quinua cocida" to this one.
  "Quinua": ["quinua", "quinoa", "quinua cocida", "quinoa cocida"],
  "Pan de molde": ["pan de molde", "pan molde"],
  "Pan pita": ["pan pita", "pita"],
  "Rapidita": ["rapidita", "tortilla de trigo"],
  "Tostada": ["tostada", "tostadas"],
  "Galletas Salmas": ["salmas"],
  "Galleta Club Social": ["club social", "galleta club"],
  // Dinner placeholder: "the fruit options" as the carb.
  "Fruta (ver opciones de fruta)": ["fruta", "frutas"],

  // ── Fats (mostly filled via defaults, but supported if mentioned) ──
  "Palta": ["palta", "aguacate"],
  "Aceite de oliva": ["aceite de oliva", "aceite"],
  "Aceitunas": ["aceituna", "aceitunas"],
  "Frutos secos": ["frutos secos", "almendras", "nueces", "mani", "maní"],

  // ── Vegetables ──
  "Verduras frescas": ["verduras frescas", "ensalada", "ensalada fresca", "lechuga", "tomate", "pepino"],
  "Verduras cocidas": ["verduras cocidas", "verduras al vapor", "vainita"],

  // ── Fruits ──
  "Arándanos": ["arandanos", "arándanos", "blueberries"],
  "Arandanos": ["arandanos", "arándanos", "blueberries"],
  "Fresa": ["fresa", "fresas"],
  "Mandarinas": ["mandarina", "mandarinas"],
};

/** Lowercase + strip diacritics. */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Find all matching option IDs within a ChoiceGroup for a note, sorted so
 * the option backed by the longest matched keyword comes first; ties broken
 * by earliest appearance in the text. Returns unique option IDs.
 *
 * Longest-match precedence handles two cases:
 *  - Overlapping keywords inside the same span: "carne molida" wins over
 *    "carne" for that range (greedy non-overlapping pass).
 *  - Disjoint keywords pointing at different options: "saltado de carne
 *    molida" prefers Carne molida over Carne de res because "carne molida"
 *    (12 chars) is more specific than "saltado" (7 chars).
 */
export function scanNoteForGroup(note: string, group: ChoiceGroup): string[] {
  const normalized = normalize(note);
  if (!normalized.trim()) return [];

  interface Keyword { needle: string; optionId: string }
  const keywords: Keyword[] = [];
  for (const opt of group.options) {
    const kws = INGREDIENT_KEYWORDS[opt.name];
    if (!kws) continue;
    for (const kw of kws) {
      const needle = normalize(kw);
      if (needle) keywords.push({ needle, optionId: opt.id });
    }
  }
  // Longest keyword first so specific phrases claim their text range before
  // shorter overlapping keywords can match it.
  keywords.sort((a, b) => b.needle.length - a.needle.length);

  interface Match { id: string; index: number; length: number }
  const matches: Match[] = [];
  const taken: Array<[number, number]> = [];

  for (const { needle, optionId } of keywords) {
    const re = new RegExp(
      `(^|[^a-z0-9])${escapeRegExp(needle)}([^a-z0-9]|$)`,
      "gi",
    );
    let m: RegExpExecArray | null;
    while ((m = re.exec(normalized)) !== null) {
      const start = m.index + (m[1]?.length ?? 0);
      const end = start + needle.length;
      const overlaps = taken.some(([s, e]) => start < e && s < end);
      if (overlaps) continue;
      taken.push([start, end]);
      matches.push({ id: optionId, index: start, length: needle.length });
    }
  }

  // Per option keep only the longest keyword that matched (most specific).
  const bestPerId = new Map<string, Match>();
  for (const m of matches) {
    const prev = bestPerId.get(m.id);
    if (!prev || m.length > prev.length) bestPerId.set(m.id, m);
  }
  const unique = Array.from(bestPerId.values());
  // Longest match wins; ties broken by earlier appearance.
  unique.sort((a, b) => b.length - a.length || a.index - b.index);
  return unique.map((m) => m.id);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Expand an ordered list of matched option IDs into `pick` slots. */
function fillSlots(
  matchedIds: string[],
  pick: number,
  canRepeat: boolean,
): Selection[] {
  const slots: (string | null)[] = [];
  if (canRepeat) {
    for (const id of matchedIds) {
      if (slots.length >= pick) break;
      slots.push(id);
    }
    if (slots.length > 0 && slots.length < pick) {
      const last = slots[slots.length - 1]!;
      while (slots.length < pick) slots.push(last);
    }
  } else {
    const seen = new Set<string>();
    for (const id of matchedIds) {
      if (slots.length >= pick) break;
      if (seen.has(id)) continue;
      seen.add(id);
      slots.push(id);
    }
  }
  return slotsToSelections(slots);
}

function slotsToSelections(slots: (string | null)[]): Selection[] {
  const counts = new Map<string, number>();
  for (const id of slots) {
    if (id === null) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const result: Selection[] = [];
  for (const [id, count] of counts) result.push({ optionId: id, count });
  return result;
}

function findOptionIdByName(options: FoodOption[], name: string): string | null {
  const o = options.find((opt) => opt.name === name);
  return o ? o.id : null;
}

/** Vegetable / fat groups default to "Verduras frescas" / "Palta" when offered; others stay empty. */
function defaultSelections(group: ChoiceGroup): ChoiceGroupSelections {
  const name =
    group.category === "vegetable" ? "Verduras frescas" : group.category === "fat" ? "Palta" : null;
  const id = name ? findOptionIdByName(group.options, name) : null;
  if (id === null) return { selections: [], skipped: false };
  return { selections: [{ optionId: id, count: group.pick }], skipped: false };
}

/**
 * Build a full `MealSelections` from a note, per the Comidas screen rules:
 *  - carb / protein: scan + fillSlots
 *  - vegetable: all slots = "Verduras frescas" (if present in options)
 *  - fat: all slots = "Palta" (if present in options)
 *  - fruit: left empty
 * Menu meals use the groups of `menuId`; the menu's fixed foods stay selected.
 */
export function buildMealSelectionsFromNote(
  note: string,
  meal: Meal,
  menuId?: string,
): MealSelections {
  const base = createMealSelections(meal, menuId);
  return getMealChoices(meal, menuId).map((group, i) => {
    if (base[i]!.selections.length > 0) return base[i]!;
    if (group.category === "carb" || group.category === "protein") {
      const ids = scanNoteForGroup(note, group);
      return {
        selections: fillSlots(ids, group.pick, group.canRepeat),
        skipped: false,
      };
    }
    return defaultSelections(group);
  });
}

type Category = ChoiceGroup["category"];

/**
 * The foods a note names, read once against every food a menu meal's menus
 * offer, per category (name → category). Reading all menus at once keeps a
 * phrase's meaning the same in each: "lomo de cerdo" is pork everywhere, not
 * "lomo" (beef) in a menu without pork.
 */
function noteFoods(note: string, menus: MealMenu[]): Map<string, Category> {
  const byCategory = new Map<Category, Map<string, FoodOption>>();
  for (const menu of menus)
    for (const group of menu.choices) {
      let options = byCategory.get(group.category);
      if (!options) byCategory.set(group.category, (options = new Map()));
      for (const o of group.options) if (!options.has(o.name)) options.set(o.name, { ...o, id: o.name });
    }
  const foods = new Map<string, Category>();
  for (const [category, options] of byCategory) {
    const group: ChoiceGroup = { category, pick: 1, canRepeat: false, options: [...options.values()] };
    for (const name of scanNoteForGroup(note, group)) foods.set(name, category);
  }
  return foods;
}

/**
 * For a menu meal with no menu chosen: the menu that offers the most of the
 * note's foods, or null when no menu offers any.
 *
 * A tie goes to the menu holding the note's protein (the protein is the
 * dish; the foods it lacks stay for the person to pick): "lomo de cerdo con
 * papa" is Opción 1 (cerdo), though papa is only in 2 and 4. It stays null
 * (genuinely ambiguous) when the protein is in several tied menus ("pollo"),
 * proteins point at different menus ("cerdo con atún"), or another tied menu
 * is the only one with one of the note's foods ("lentejas con cerdo":
 * lentejas only in 3, cerdo only in 1).
 */
export function pickMenuForNote(note: string, meal: Meal): string | null {
  const { scored, tied, foods } = scoreMenus(note, meal);
  if (tied.length === 0) return null;
  if (tied.length === 1) return tied[0]!.id;

  const withProtein = tied.filter((s) => s.matched.some((name) => foods.get(name) === "protein"));
  if (withProtein.length !== 1) return null;
  const winner = withProtein[0]!;
  const onlyIn = (name: string) => scored.filter((s) => s.matched.includes(name)).length === 1;
  const contested = tied.some((s) => s !== winner && s.matched.some(onlyIn));
  return contested ? null : winner.id;
}

/**
 * The menus a note could mean when `pickMenuForNote` can't decide: the menus
 * tied for most of the note's foods, narrowed to those holding the note's
 * protein when any does. Empty when no menu offers any of its foods.
 */
export function menuCandidatesForNote(note: string, meal: Meal): string[] {
  const { tied, foods } = scoreMenus(note, meal);
  const withProtein = tied.filter((s) => s.matched.some((name) => foods.get(name) === "protein"));
  return (withProtein.length > 0 ? withProtein : tied).map((s) => s.id);
}

/** Each menu with the note's foods it offers, and the menus tied for the most (none when the best is 0). */
function scoreMenus(note: string, meal: Meal) {
  const menus = meal.menus ?? [];
  const foods = noteFoods(note, menus);
  const scored = menus.map((menu) => {
    const offered = new Set(menu.choices.flatMap((g) => g.options.map((o) => o.name)));
    return { id: menu.id, matched: [...foods.keys()].filter((name) => offered.has(name)) };
  });
  const best = Math.max(0, ...scored.map((s) => s.matched.length));
  const tied = best === 0 ? [] : scored.filter((s) => s.matched.length === best);
  return { scored, tied, foods };
}

/**
 * The menu a note-driven fill should use: `undefined` for regular meals;
 * `null` means "menu meal with nothing to go on": leave the meal alone.
 *
 * With no menu chosen, it's the note's unique best match. With one chosen,
 * the chosen menu stays, unless it only came from the previous note (it is
 * the menu `previousNote` picks: autofill, or lunch mirrored into dinner) and
 * the new note uniquely matches another menu: the note moved on, so the
 * menu follows. A menu the person picked by hand (no note, or a different
 * one than the note would pick) is never overridden. Without
 * `previousNote` the chosen menu always stays.
 */
export function menuForNote(
  note: string,
  meal: Meal,
  chosenMenuId: string | undefined,
  previousNote?: string,
): string | undefined | null {
  if (!meal.menus) return undefined;
  const picked = pickMenuForNote(note, meal);
  if (chosenMenuId === undefined) return picked;
  const chosenByNote = previousNote !== undefined && pickMenuForNote(previousNote, meal) === chosenMenuId;
  return chosenByNote && picked !== null ? picked : chosenMenuId;
}

/**
 * A saved note that left its meal with no carb or protein (a menu meal: no
 * menu, or none picked in it): Comidas tells the person to pick it by hand.
 */
export function noteFilledNothing(
  note: string,
  meal: Meal,
  menuId: string | undefined,
  sels: MealSelections,
): boolean {
  if (!note.trim()) return false;
  return !getMealChoices(meal, menuId).some(
    (group, i) => (group.category === "carb" || group.category === "protein") && (sels[i]?.selections.length ?? 0) > 0,
  );
}

/** Chosen menu ids of the lunch and dinner meals (menu meals only). */
export interface LunchDinnerMenus {
  lunch?: string;
  dinner?: string;
}

export interface DinnerFromLunchResult {
  feasible: boolean;
  /** Ingredient name that couldn't be matched in dinner, if infeasible. */
  missing?: string;
  /** Full dinner MealSelections (only meaningful when feasible). */
  selections: MealSelections;
}

/**
 * Mirror a lunch meal's selections onto a dinner meal. Matching is done by
 * FoodOption.name (since option IDs differ between MAIN_CARBS/DINNER_CARBS,
 * GYM_LUNCH_PROTEINS/FULL_PROTEINS, etc.). Vegetable/fat default to
 * "Verduras frescas" and "Palta" just like lunch. Fruit left empty.
 *
 * Feasibility requires: lunch has at least one carb selection AND at least
 * one protein selection, AND every selected lunch carb/protein name can be
 * found by name in the dinner equivalent group.
 */
export function buildDinnerFromLunch(
  lunchMeal: Meal,
  lunchSelections: MealSelections,
  dinnerMeal: Meal,
  menus: LunchDinnerMenus = {},
): DinnerFromLunchResult {
  const lunchChoices = getMealChoices(lunchMeal, menus.lunch);
  const dinnerChoices = getMealChoices(dinnerMeal, menus.dinner);
  const emptyDinner = () => emptyMealSelections(dinnerMeal, menus.dinner);
  const lunchCarbIdx = lunchChoices.findIndex((c) => c.category === "carb");
  const lunchProtIdx = lunchChoices.findIndex((c) => c.category === "protein");
  const dinnerCarbIdx = dinnerChoices.findIndex((c) => c.category === "carb");
  const dinnerProtIdx = dinnerChoices.findIndex((c) => c.category === "protein");

  // If any side is missing a carb or protein group, we can't really repeat.
  if (
    lunchCarbIdx === -1 ||
    lunchProtIdx === -1 ||
    dinnerCarbIdx === -1 ||
    dinnerProtIdx === -1
  ) {
    return {
      feasible: false,
      missing: "carbohidrato o proteína",
      selections: emptyDinner(),
    };
  }

  const lunchCarbGroup = lunchChoices[lunchCarbIdx]!;
  const lunchProtGroup = lunchChoices[lunchProtIdx]!;
  const lunchCarbSel = lunchSelections[lunchCarbIdx];
  const lunchProtSel = lunchSelections[lunchProtIdx];

  if (!lunchCarbSel || lunchCarbSel.selections.length === 0) {
    return {
      feasible: false,
      missing: "carbohidrato",
      selections: emptyDinner(),
    };
  }
  if (!lunchProtSel || lunchProtSel.selections.length === 0) {
    return {
      feasible: false,
      missing: "proteína",
      selections: emptyDinner(),
    };
  }

  const dinnerCarbGroup = dinnerChoices[dinnerCarbIdx]!;
  const dinnerProtGroup = dinnerChoices[dinnerProtIdx]!;

  const carbMapped = mapSelectionsToDinnerGroup(
    lunchCarbSel.selections,
    lunchCarbGroup.options,
    dinnerCarbGroup,
  );
  if (!carbMapped.ok) {
    return {
      feasible: false,
      missing: carbMapped.missing,
      selections: emptyDinner(),
    };
  }
  const protMapped = mapSelectionsToDinnerGroup(
    lunchProtSel.selections,
    lunchProtGroup.options,
    dinnerProtGroup,
  );
  if (!protMapped.ok) {
    return {
      feasible: false,
      missing: protMapped.missing,
      selections: emptyDinner(),
    };
  }

  // Feasible — build the full MealSelections (a menu's fixed foods stay selected).
  const base = emptyDinner();
  const selections: MealSelections = dinnerChoices.map((group, i) => {
    if (i === dinnerCarbIdx) return { selections: carbMapped.selections, skipped: false };
    if (i === dinnerProtIdx) return { selections: protMapped.selections, skipped: false };
    if (base[i]!.selections.length > 0) return base[i]!;
    return defaultSelections(group);
  });

  return { feasible: true, selections };
}

/**
 * Map an ordered list of lunch selections onto a dinner ChoiceGroup. Matches
 * are done by FoodOption.name. The lunch selections are expanded slot-by-slot
 * and then re-filled using the dinner group's own `pick` / `canRepeat` — so
 * a single lunch protein can fill all 5 dinner protein slots via the normal
 * repeat-last-to-fill rule.
 */
function mapSelectionsToDinnerGroup(
  lunchSelections: Selection[],
  lunchOptions: FoodOption[],
  dinnerGroup: ChoiceGroup,
):
  | { ok: true; selections: Selection[] }
  | { ok: false; missing: string } {
  const dinnerIds: string[] = [];
  for (const sel of lunchSelections) {
    const lunchOpt = lunchOptions.find((o) => o.id === sel.optionId);
    if (!lunchOpt) return { ok: false, missing: "desconocido" };
    const dinnerOpt = dinnerGroup.options.find((o) => o.name === lunchOpt.name);
    if (!dinnerOpt) return { ok: false, missing: lunchOpt.name };
    for (let i = 0; i < sel.count; i++) dinnerIds.push(dinnerOpt.id);
  }
  return {
    ok: true,
    selections: fillSlots(dinnerIds, dinnerGroup.pick, dinnerGroup.canRepeat),
  };
}

/** Fresh selections for a meal (a menu's fixed foods pre-selected). */
export function emptyMealSelections(meal: Meal, menuId?: string): MealSelections {
  return createMealSelections(meal, menuId);
}

/**
 * True if a dinner meal's carb+protein selections match what
 * `buildDinnerFromLunch` would produce for the current lunch. Used to seed
 * the "Repetir en cena" checkbox when the screen first mounts or renders.
 */
export function dinnerMirrorsLunch(
  lunchMeal: Meal,
  lunchSelections: MealSelections,
  dinnerMeal: Meal,
  dinnerSelections: MealSelections,
  menus: LunchDinnerMenus = {},
): boolean {
  const res = buildDinnerFromLunch(lunchMeal, lunchSelections, dinnerMeal, menus);
  if (!res.feasible) return false;

  // Only require the carb and protein groups to match — vegetable/fat
  // defaults are part of the "repeat" fill but if the user tweaks them
  // manually we still consider the meal "repeated".
  const dinnerChoices = getMealChoices(dinnerMeal, menus.dinner);
  const carbIdx = dinnerChoices.findIndex((c) => c.category === "carb");
  const protIdx = dinnerChoices.findIndex((c) => c.category === "protein");
  if (carbIdx === -1 || protIdx === -1) return false;
  const carbSel = dinnerSelections[carbIdx];
  const protSel = dinnerSelections[protIdx];
  if (!carbSel || !protSel) return false;
  return (
    selectionsEqual(res.selections[carbIdx]!.selections, carbSel.selections) &&
    selectionsEqual(res.selections[protIdx]!.selections, protSel.selections)
  );
}

function selectionsEqual(a: Selection[], b: Selection[]): boolean {
  if (a.length !== b.length) return false;
  const key = (s: Selection) => `${s.optionId}:${s.count}`;
  const setA = a.map(key).sort();
  const setB = b.map(key).sort();
  for (let i = 0; i < setA.length; i++) {
    if (setA[i] !== setB[i]) return false;
  }
  return true;
}

// ── Saving a lunch / dinner note (Comidas) ──

/** A day's lunch and dinner as the Comidas screen sees them. */
export interface LunchDinnerState {
  lunchMeal: Meal;
  dinnerMeal: Meal;
  lunchSels: MealSelections;
  dinnerSels: MealSelections;
  lunchNote: string;
  dinnerNote: string;
  lunchMenuId?: string;
  dinnerMenuId?: string;
}

/** What saving a note changes in one meal: its menu (menu meals) and selections. */
export interface MealFill {
  menuId: string | undefined;
  selections: MealSelections;
}

/**
 * Dinner is the lunch's to fill only while it is empty (no note, menu or
 * picks) or still the mirror of the current lunch (same note, same carb and
 * protein as `buildDinnerFromLunch` gives). A dinner with its own note, menu
 * or hand picks is the person's: lunch never touches it.
 */
export function dinnerFollowsLunch(s: LunchDinnerState): boolean {
  const empty =
    !s.dinnerNote.trim() &&
    s.dinnerMenuId === undefined &&
    s.dinnerSels.every((cg) => cg.selections.length === 0);
  if (empty) return true;
  return (
    s.dinnerNote.trim() === s.lunchNote.trim() &&
    dinnerMirrorsLunch(s.lunchMeal, s.lunchSels, s.dinnerMeal, s.dinnerSels, {
      lunch: s.lunchMenuId,
      dinner: s.dinnerMenuId,
    })
  );
}

/**
 * Saving `note` on lunch: the lunch fill (null: a menu meal the note can't
 * place stays as is) and, when dinner follows lunch, the dinner mirror with
 * the same note. The Comidas preview shows exactly this.
 */
export function planLunchNote(
  s: LunchDinnerState,
  note: string,
): { lunch: MealFill | null; dinner: (MealFill & { note: string }) | null } {
  const lunchMenu = menuForNote(note, s.lunchMeal, s.lunchMenuId, s.lunchNote);
  if (lunchMenu === null) return { lunch: null, dinner: null };
  const lunch = { menuId: lunchMenu, selections: buildMealSelectionsFromNote(note, s.lunchMeal, lunchMenu) };
  if (!dinnerFollowsLunch(s)) return { lunch, dinner: null };
  const dinnerMenu = menuForNote(note, s.dinnerMeal, s.dinnerMenuId, s.dinnerNote);
  if (dinnerMenu === null) return { lunch, dinner: null };
  const res = buildDinnerFromLunch(s.lunchMeal, lunch.selections, s.dinnerMeal, {
    lunch: lunchMenu,
    dinner: dinnerMenu,
  });
  return { lunch, dinner: res.feasible ? { menuId: dinnerMenu, selections: res.selections, note } : null };
}

/** Saving `note` on dinner: its fill, or null when a menu meal can't place it. */
export function planDinnerNote(
  dinnerMeal: Meal,
  dinnerMenuId: string | undefined,
  previousNote: string,
  note: string,
): MealFill | null {
  const menuId = menuForNote(note, dinnerMeal, dinnerMenuId, previousNote);
  if (menuId === null) return null;
  return { menuId, selections: buildMealSelectionsFromNote(note, dinnerMeal, menuId) };
}
